import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../../utils/api";

// ─── Animation Variants ────────────────────────────────────────────────────

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const fadeUp = {
  hidden:  { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const fadeIn = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || "?";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

// ─── Sub-components ────────────────────────────────────────────────────────

const Chip = ({ children }) => (
  <span style={{
    display: "inline-flex", alignItems: "center",
    background: "#F1F5F9", color: "#475569",
    fontSize: 12, fontWeight: 500,
    padding: "3px 10px", borderRadius: 99,
    letterSpacing: "0.01em",
  }}>
    {children}
  </span>
);

const StatusBadge = ({ active = true }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    background: active ? "#DCFCE7" : "#FEE2E2",
    color: active ? "#15803D" : "#B91C1C",
    fontSize: 12, fontWeight: 600,
    padding: "4px 10px", borderRadius: 99,
  }}>
    <span style={{
      width: 6, height: 6, borderRadius: "50%",
      background: active ? "#22C55E" : "#EF4444",
      display: "inline-block",
    }} />
    {active ? "Active" : "Inactive"}
  </span>
);

function InfoBlock({ icon, label, value }) {
  return (
    <motion.div variants={fadeUp} style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      padding: "16px 0",
      borderBottom: "1px solid #F1F5F9",
    }}>
      {/* Icon box */}
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <p style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500,
          letterSpacing: "0.06em", textTransform: "uppercase", margin: 0 }}>
          {label}
        </p>
        <p style={{ fontSize: 14, color: "#0F172A", fontWeight: 500, margin: "3px 0 0" }}>
          {value || "—"}
        </p>
      </div>
    </motion.div>
  );
}

// ─── Icons (inline SVG, no dependency) ────────────────────────────────────

const IconEmail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);
const IconLocation = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconDept = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
  </svg>
);
const IconPhone = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.06 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.12a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92Z"/>
  </svg>
);

// ─── Main Component ────────────────────────────────────────────────────────

export default function E_Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/current-user", { withCredentials: true })
      .then((res) => setProfile(res.data))
      .catch(() => setProfile({}))
      .finally(() => setLoading(false));
  }, []);

  const initials = getInitials(profile?.name);

  // Skeleton pulse
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
          <div style={{ height: 8, background: "#0F172A" }} />
          <div style={{ padding: "40px 40px 32px", display: "flex", gap: 24, alignItems: "center" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#E2E8F0" }} />
            <div>
              <div style={{ width: 160, height: 20, borderRadius: 6, background: "#E2E8F0", marginBottom: 8 }} />
              <div style={{ width: 100, height: 14, borderRadius: 6, background: "#F1F5F9" }} />
            </div>
          </div>
          <div style={{ padding: "0 40px 40px" }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ height: 52, borderBottom: "1px solid #F1F5F9",
                display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#F1F5F9" }} />
                <div>
                  <div style={{ width: 60, height: 10, borderRadius: 4, background: "#F1F5F9", marginBottom: 6 }} />
                  <div style={{ width: 120, height: 14, borderRadius: 4, background: "#E2E8F0" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
       

        {/* ── Header ── */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          style={{ padding: "36px 40px 28px" }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 22 }}>
            {/* Avatar */}
            <motion.div variants={fadeIn} style={{ position: "relative", flexShrink: 0 }}>
              <div style={{
                width: 76, height: 76, borderRadius: "50%",
                background: "#0F172A",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26, fontWeight: 700, color: "#F8FAFC",
                letterSpacing: "-0.5px",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {initials}
              </div>
              {/* Online indicator */}
              <div style={{
                position: "absolute", bottom: 3, right: 3,
                width: 14, height: 14, borderRadius: "50%",
                background: "#22C55E",
                border: "2.5px solid white",
              }} />
            </motion.div>

            {/* Name & role */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <motion.div variants={fadeUp} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h1 style={{
                  fontSize: 22, fontWeight: 700, color: "#0F172A",
                  margin: 0, letterSpacing: "-0.4px",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {profile?.name || "—"}
                </h1>
                <StatusBadge active />
              </motion.div>

              <motion.div variants={fadeUp} style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {profile?.designation && <Chip>{profile.designation}</Chip>}
                {profile?.department  && <Chip>{profile.department}</Chip>}
                {profile?.employeeId  && (
                  <Chip>ID: {profile.employeeId}</Chip>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <div style={{ height: 1, background: "#F1F5F9", margin: "0 40px" }} />

        {/* ── Info grid ── */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          style={{ padding: "8px 40px 36px" }}
        >
          <motion.p variants={fadeUp} style={{
            fontSize: 11, fontWeight: 600, color: "#94A3B8",
            letterSpacing: "0.08em", textTransform: "uppercase",
            margin: "20px 0 4px",
          }}>
            Profile Information
          </motion.p>

          <InfoBlock icon={<IconEmail />}    label="Email"              value={profile?.email} />
          <InfoBlock icon={<IconUser />}     label="Reporting Manager"  value={profile?.reportingManager} />
          <InfoBlock icon={<IconCalendar />} label="Date of Joining"    value={formatDate(profile?.dateOfJoining)} />
          <InfoBlock icon={<IconLocation />} label="Location"           value={profile?.location} />
          {profile?.phone      && <InfoBlock icon={<IconPhone />}    label="Phone"       value={profile.phone} />}
          {profile?.department && <InfoBlock icon={<IconDept />}     label="Department"  value={profile.department} />}
        </motion.div>

        {/* ── Footer ── */}
        <div style={{
          background: "#FAFAFA",
          borderTop: "1px solid #F1F5F9",
          borderRadius: "0 0 16px 16px",
          padding: "14px 40px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
         
          
        </div>
      </motion.div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "#F1F5F9",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  card: {
    background: "#FFFFFF",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(15,23,42,0.06)",
    width: "100%",
    maxWidth: 520,
    overflow: "hidden",
  },
};